<script setup>
  import { useVirtualList } from '@vueuse/core';
  import { ref, computed, onMounted, onBeforeUnmount } from 'vue';
  import CarouselContainer from '~/components/carousel/CarouselContainer.vue';
  import InsiderCarouselCard from '~/components/insider/InsiderCarouselCard.vue';

  const props = defineProps({
    articleList: {
      type: Array,
      required: true,
    },
    itemType: {
      type: String,
      required: true,
      validator: (value) => {
        return [
          'business-series-webinar',
          'latest-economic-articles',
          'executive-series-webinar',
          'trends-talk',
          'recent-announcements',
          'itr-academy',
        ].includes(value);
      },
    },
  });

  const emit = defineEmits(['product-selected']);

  const screenWidth = ref(window.innerWidth);
  const filteredArticleList = computed(() => props.articleList);
  const computedItemWidth = computed(() => {
    const isScreenMedium = screenWidth.value >= 768 && screenWidth.value < 1200;
    const isScreenWide = screenWidth.value >= 1200;

    if (
      props.itemType === 'business-series-webinar' ||
      props.itemType === 'executive-series-webinar'
    ) {
      if (isScreenMedium) return 276;
      if (isScreenWide) return 399;
      return 222;
    } else if (props.itemType === 'latest-economic-articles') {
      if (isScreenMedium) return 282;
      if (isScreenWide) return 340;
      return 216;
    } else if (props.itemType === 'trends-talk') {
      if (isScreenMedium) return 276;
      if (isScreenWide) return 399;
      return 216;
    } else if (props.itemType === 'itr-academy') {
      if (isScreenMedium) return 276;
      if (isScreenWide) return 401;
      return 222;
    }

    return isScreenWide ? 490 : 300;
  });
  const gapMinusLastItem = computed(() => {
    if (props.articleList.length <= 0) return 0;
    return 32;
  });
  const computedTotalWidth = () =>
    computedItemWidth.value + gapMinusLastItem.value; // the gap between items must be included

  const { list, containerProps, wrapperProps } = useVirtualList(
    filteredArticleList,
    {
      itemWidth: computedTotalWidth(),
    }
  );

  onMounted(() => {
    window.addEventListener('resize', () => {
      screenWidth.value = window.innerWidth;
    });
  });
  onBeforeUnmount(() => {
    window.removeEventListener('resize', () => {
      screenWidth.value = window.innerWidth;
    });
  });
</script>

<template>
  <CarouselContainer
    :id="`insider-carousel-${props.itemType}`"
    :key="`insider-carousel-${props.itemType}`"
    class="insider-carousel-container"
    :container-props="containerProps"
    :wrapper-props="wrapperProps"
  >
    <InsiderCarouselCard
      v-for="(item, index) in list"
      :id="`${props.itemType}-${item.data.id}`"
      :key="item.index"
      :product="item.data"
      :product-type="props.itemType"
      :style="{
        width: `${computedItemWidth}px`,
        minWidth: `${computedItemWidth}px`,
        marginRight: index !== list.length - 1 ? `${gapMinusLastItem}px` : '0',
      }"
      @product-selected="emit('product-selected', $event)"
    />
  </CarouselContainer>
</template>

<style lang="scss" scoped>
  .insider-carousel-container {
    margin-bottom: 4rem;
    min-height: 150px;

    :deep(.carousel-container__inner) {
      padding: 1rem 0;
      // gap: 0;
    }
  }
</style>
